import moment from "moment-timezone";
import { applyTimezone, combineDateAndTime } from "../../../utils/helpers/date";
import { weekdays as allWeekdays } from "../../../components/shared/inputs/WeekdaySelect/types";

export const getWeekdays = () => {
    const firstDay = moment.localeData().firstDayOfWeek();
    return [...allWeekdays.slice(firstDay), ...allWeekdays.slice(0, firstDay)];
};

export const getAvailabilityText = (entry, t) => {
    const available = entry?.isAvailable !== false;
    if (entry?.allDay && available) return t("all-day-available");
    if (!entry?.allDay && available) return t("not-all-day-available");
    if (!entry?.allDay && !available) return t("not-all-day-unavailable");
    return t("all-day-unavailable");
};

// Stored records fill the whole day with contiguous segments (the requested windows plus
// their gaps), each flagged with `available` - keep only the windows that were requested.
const pickRequestedIntervals = (intervals, isAvailable) => {
    if (!intervals || intervals.length === 0) return [];
    return intervals.filter((interval) => interval.available === isAvailable);
};

const toFormIntervals = (intervals, date, timezone) => {
    if (!intervals || intervals.length === 0) {
        return [{ from: undefined, to: undefined }];
    }

    return intervals.map((interval) => {
        let from = combineDateAndTime(moment(date), moment.parseZone(interval.from));
        let to = combineDateAndTime(moment(date), moment.parseZone(interval.to));
        if (timezone) {
            from = applyTimezone(from, timezone);
            to = applyTimezone(to, timezone);
        }
        return { from: from.toISOString(true), to: to.toISOString(true) };
    });
};

export const toFormDay = (entry, { date, timezone, withDates = false } = {}) => {
    const isAvailable = entry?.isAvailable ?? true;

    const value = {
        allDay: entry?.allDay ?? false,
        isAvailable,
        reason: entry?.reason || undefined,
        intervals: toFormIntervals(pickRequestedIntervals(entry?.intervals, isAvailable), date, timezone),
    };

    if (withDates) {
        value.startDate = entry?.startDate ? moment(entry.startDate).toDate() : undefined;
        value.endDate = entry?.endDate ? moment(entry.endDate).toDate() : undefined;
    }

    return value;
};

export const selectDayEntries = (entries, day) => {
    const forDay = (entries || []).filter((entry) => entry.day === day);

    // A day can hold several pending records (each new request adds one), so edit the
    // latest; on equal or missing timestamps the later record in the response wins.
    const stamp = (entry) => moment(entry.updatedAt || entry.createdAt || 0).valueOf();
    const formEntry = forDay
        .filter((entry) => entry.status === "pending")
        .reduce((latest, entry) => (!latest || stamp(entry) >= stamp(latest) ? entry : latest), undefined);

    const approved = forDay
        .filter((entry) => entry.status === "approved")
        .map((entry) => ({
            allDay: entry.allDay,
            isAvailable: entry.isAvailable,
            reason: entry.reason,
            intervals: pickRequestedIntervals(entry.intervals, entry.isAvailable ?? true),
        }));

    // The audit trail for the day, pooled from every sibling record.
    const history = forDay.flatMap((entry) => entry.history || []);

    return { formEntry, approved, history };
};

export const toPayloadDay = (day, dayData, { type, date, timezone }) => {
    const base = {
        allDay: dayData.allDay,
        isAvailable: dayData.isAvailable,
        reason: dayData.reason,
        type,
        day,
        startDate: dayData.startDate ? moment(dayData.startDate).format("YYYY-MM-DD") : undefined,
        endDate: dayData.endDate ? moment(dayData.endDate).format("YYYY-MM-DD") : undefined,
    };

    const validIntervals = (dayData.intervals || []).filter((interval) => interval?.from && interval?.to);

    if (validIntervals.length > 0 && !dayData.allDay) {
        return {
            ...base,
            intervals: validIntervals
                .sort((a, b) => moment(a.from).diff(moment(b.from), "seconds"))
                .map(({ from, to }) => ({
                    from: combineDateAndTime(date, moment.parseZone(from), timezone).toISOString(true),
                    to: combineDateAndTime(date, moment.parseZone(to), timezone).toISOString(true),
                })),
        };
    }

    if (dayData.allDay || !dayData.isAvailable) {
        return { ...base, intervals: [] };
    }

    return null;
};

// Time-of-day for intervals and day granularity for dates, so values that survive a
// server round-trip (or were picked on a different base date) still read as unchanged.
const time = (value) => (value ? moment.parseZone(value).format("HH:mm") : "");
const dayStamp = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

export const serializeDay = (day) => {
    if (!day) return "";
    const intervals = (Array.isArray(day.intervals) ? day.intervals : [])
        .map((interval) => `${time(interval?.from)}-${time(interval?.to)}`)
        .filter((interval) => interval !== "-")
        .sort()
        .join(",");

    return [
        day.allDay ? 1 : 0,
        day.isAvailable === false ? 0 : 1,
        (day.reason || "").trim(),
        dayStamp(day.startDate),
        dayStamp(day.endDate),
        intervals,
    ].join("|");
};

export const hasDayChanges = (current, initial, days) =>
    days.some((day) => serializeDay(current?.[day]) !== serializeDay(initial?.[day]));

// Past days without an existing entry are read-only.
export const isDayLocked = (day, entry) => moment(day).isBefore(moment(), "day") && !entry;
